/**
 * Meta (Facebook) Pixel + Conversions API client helper.
 *
 * Pairs the browser-side fbq() Pixel call with a server-side CAPI event
 * (posted to /api/meta-capi) sharing the same event_id, so Meta can
 * deduplicate the two signals. See api/meta-capi.js for the server leg.
 */

export function generateEventId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `evt_${crypto.randomUUID()}`;
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function getCookie(name) {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

export function getMetaBrowserIds() {
  return { fbp: getCookie('_fbp'), fbc: getCookie('_fbc') };
}

export function ensureFbc() {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get('fbclid');
  if (!fbclid) return;

  const existing = getCookie('_fbc');
  if (existing && existing.endsWith(fbclid)) return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; max-age=7776000; path=/; SameSite=Lax${secure}`;
}

export function trackEvent(eventName, { customData = {}, userData = {}, eventId } = {}) {
  if (typeof window === 'undefined') return undefined;

  const id = eventId || generateEventId();

  try {
    if (window.fbq) {
      window.fbq('track', eventName, customData, { eventID: id });
    }
  } catch {
    // Third-party tracking must never interrupt the visitor journey.
  }

  try {
    if (typeof fetch === 'function') {
      const request = fetch('/api/meta-capi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventName,
          eventId: id,
          eventSourceUrl: window.location.href,
          userData,
          customData,
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
        }),
        keepalive: true,
      });

      if (request && typeof request.catch === 'function') {
        request.catch(() => {});
      }
    }
  } catch {
    // Third-party tracking must never interrupt the visitor journey.
  }

  return id;
}

export function trackCustomEvent(eventName, { customData = {}, userData = {}, eventId } = {}) {
  if (typeof window === 'undefined') return undefined;

  const id = eventId || generateEventId();

  try {
    if (window.fbq) {
      window.fbq('trackCustom', eventName, customData, { eventID: id });
    }
  } catch {
    // Third-party tracking must never interrupt the visitor journey.
  }

  try {
    if (typeof fetch === 'function') {
      const request = fetch('/api/meta-capi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          eventName,
          eventId: id,
          eventSourceUrl: window.location.href,
          userData,
          customData,
          fbp: getCookie('_fbp'),
          fbc: getCookie('_fbc'),
        }),
        keepalive: true,
      });

      if (request && typeof request.catch === 'function') {
        request.catch(() => {});
      }
    }
  } catch {
    // Third-party tracking must never interrupt the visitor journey.
  }

  return id;
}

export function trackPageView() {
  return trackEvent('PageView');
}
